import { apiClient, apiFetch, bearer, unwrap } from './client';
import type { components } from './types';

export type GlAccountOut = components['schemas']['GlAccountOut'];
export type GlAccountListOut = components['schemas']['GlAccountListOut'];
export type GlAccountStatsOut = components['schemas']['GlAccountStatsOut'];
export type GlAccountClassifyIn = components['schemas']['GlAccountClassifyIn'];
export type GlAccountClassifyOut = components['schemas']['GlAccountClassifyOut'];
export type GlAccountDiscoverOut = components['schemas']['GlAccountDiscoverOut'];
export type GlAccountImportOut = components['schemas']['GlAccountImportOut'];

export type TransactionCategory = NonNullable<GlAccountClassifyIn['category']>;

/** Runtime mirror of the API's closed category vocabulary, for the
 *  workbench's category select. Kept in registration order; the
 *  schema type is the compile-time source of truth. */
export const TRANSACTION_CATEGORIES: TransactionCategory[] = [
  'meals_and_entertainment',
  'interest_expense',
  'accrual_no_invoice',
  'exempt_capital_transaction',
  'uncategorized',
];

export function listGlAccounts(
  slug: string,
  idToken: string,
  opts: { limit?: number; offset?: number } = {},
): Promise<GlAccountListOut> {
  return apiClient
    .GET('/api/customers/{slug}/gl-accounts', {
      params: { path: { slug }, query: opts },
      headers: bearer(idToken),
    })
    .then(unwrap<GlAccountListOut>);
}

/** Loops pages until a short one — the workbench wants the whole chart. */
export async function listAllGlAccounts(
  slug: string,
  idToken: string,
): Promise<GlAccountListOut> {
  const limit = 200;
  let offset = 0;
  const items: GlAccountOut[] = [];
  let stats: GlAccountStatsOut = { total: 0, classified: 0, unclassified_debit_total: '0' };
  for (;;) {
    const page = await listGlAccounts(slug, idToken, { limit, offset });
    items.push(...page.items);
    stats = page.stats;
    if (page.next_offset == null) {
      return { items, next_offset: null, stats };
    }
    offset = page.next_offset;
  }
}

export function classifyGlAccounts(
  slug: string,
  body: GlAccountClassifyIn,
  idToken: string,
): Promise<GlAccountClassifyOut> {
  return apiClient
    .PATCH('/api/customers/{slug}/gl-accounts', {
      params: { path: { slug } },
      body,
      headers: bearer(idToken),
    })
    .then(unwrap<GlAccountClassifyOut>);
}

export function discoverGlAccounts(
  slug: string,
  idToken: string,
): Promise<GlAccountDiscoverOut> {
  return apiClient
    .POST('/api/customers/{slug}/gl-accounts/discover', {
      params: { path: { slug } },
      headers: bearer(idToken),
    })
    .then(unwrap<GlAccountDiscoverOut>);
}

/** Multipart upload — on the legacy `apiFetch` path (openapi-fetch
 *  doesn't compose with `FormData` ergonomically). Pass
 *  `dryRun: true` for the preview-before-commit shape. */
export async function importGlAccounts(
  slug: string,
  file: File,
  idToken: string,
  dryRun = false,
): Promise<GlAccountImportOut> {
  const form = new FormData();
  form.append('file', file);
  return apiFetch<GlAccountImportOut>(
    `/api/customers/${encodeURIComponent(slug)}/gl-accounts/import?dry_run=${dryRun}`,
    {
      idToken,
      method: 'POST',
      body: form,
    },
  );
}

export type GlAccountCreateIn = components['schemas']['GlAccountCreateIn'];

export function createGlAccount(
  slug: string,
  body: GlAccountCreateIn,
  idToken: string,
): Promise<GlAccountOut> {
  return apiClient
    .POST('/api/customers/{slug}/gl-accounts', {
      params: { path: { slug } },
      body,
      headers: bearer(idToken),
    })
    .then(unwrap<GlAccountOut>);
}
